/**
 * User Detail Page Component
 * Shows read-only user info and editable fields
 * Following the pattern from LocationDetail.tsx
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../../../theme/useTheme';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, TextInput, Modal } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import AdminButton from '../../components/AdminButton';
import AdminToggle from '../../components/AdminToggle';
import { AdminFormField, AdminTextInput } from '../../components/AdminFormField';
import LhDatePicker from '../../../../components/LhDatePicker';
import { createApiClient } from '../../../../api/apiClient';
import { getAccessibleModules } from '../../../../api/ticketApi';
import { AdminColors } from '../../constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import { User } from './userManagementTypes';
import type { UserUpdateRequest, UserUpdatePayload } from './userManagementTypes';
import { formatDate, isFutureDate, formatDateToString } from './userManagementUtils';
import UserFormSelect from './UserFormSelect';
import { ACCESS_TYPE_TIERS,ROLE_SCOPE_MAP, SUPPORT_ROLE_NAMES,AR_ACCESS_TYPES, mapScopeTypeToRoleScope, buildScopeConfigFromType } from '../../../TicketingPortal/components/SubmitTicketFormPanel';
import useSupportTierRoles from './hooks/useSupportTierRoles';
import AccessRequestLocationPicker, {
  LocationPickerType,
  LocationRegion,
} from '../../../TicketingPortal/components/AccessRequestLocationPicker';
import {
  selectSelectedUser,
  selectHasUnsavedChanges,
  updateSelectedUser,
  discardChanges,
} from '../../../../store/slices/userManagementSlice';
import type { RootState, AppDispatch } from '../../../../store';

interface UserDetailPageProps {
  onBack: () => void;
  onSave: (request: UserUpdateRequest) => Promise<boolean>;
  onRemoveAccess?: (user: User) => Promise<boolean>;
  locationOptions?: string[];
  /** PQGC E-Business Member (story 317066): view-only, all edit controls disabled. */
  readOnly?: boolean;
}

type DepartmentRoleOption = {
  roleId: number;
  roleName: string;
  scopeType?: string;
};

type LocationMappingPayload = {
  roleId: number;
  scopeType?: 'SITE' | 'MARKET' | 'REGION';
  locationId?: number;
  scopeRegion?: string;
  scopeMarket?: string;
};

const DEPARTMENT_OPTIONS: Record<string, string[]> = {
  PBNA: ['Warehouse', 'Manufacturing', 'Delivery', 'Warehouse Delivered'],
  FLNA: ['Warehouse Delivered', 'Manufacturing', 'Delivery'],
  '1NA': ['Warehouse', 'Delivery'],
};

// Role IDs for privileged access types — same as Add User flow
const PRIVILEGED_ROLE_IDS: Record<string, number> = {
  'Super User': 1,
  'Developer': 15,
};

const isSupportRoleName = (name?: string | null): boolean =>
  !!name && (SUPPORT_ROLE_NAMES as readonly string[]).includes(name);

const normalizeRoleName = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');

// Derives scope (full/region/market/site) + labels from the scopeType returned by
// /admin/users/roles for the given role name — no hardcoded per-role-name map.
const getRoleScopeConfig = (roleName?: string | null, roles: DepartmentRoleOption[] = []) => {
  if (!roleName) return null;
  const normalized = normalizeRoleName(roleName);
  const match = roles.find((role) => normalizeRoleName(role.roleName) === normalized);
  const scopeType = match ? mapScopeTypeToRoleScope(match.scopeType) : null;
  return scopeType ? buildScopeConfigFromType(scopeType) : null;
};

const isSameText = (left: string, right: string) => left.trim().toLowerCase() === right.trim().toLowerCase();

const extractLocationRows = (response: any): Array<any> => {
  const root = response?.data ?? response;
  return Array.isArray(root)
    ? root
    : Array.isArray(root?.content)
    ? root.content
    : Array.isArray(root?.data?.content)
    ? root.data.content
    : Array.isArray(root?.data)
    ? root.data
    : [];
};

const buildLocationMappings = (
  scopeType: 'full' | 'region' | 'market' | 'site',
  selectedLocations: string[],
  roleId: number,
  locationRows: Array<any>,
  locationTree?: LocationRegion[],
): { mappings: LocationMappingPayload[]; error?: string } => {
  if (scopeType === 'full') {
    return { mappings: [{ roleId }] };
  }

  const normalizedSelections = Array.from(
    new Set(selectedLocations.map((selection) => selection.trim()).filter(Boolean)),
  );

  if (!normalizedSelections.length) {
    return { mappings: [], error: 'Select at least one location for the chosen role scope.' };
  }

  if (scopeType === 'region') {
    return {
      mappings: normalizedSelections.map((region) => ({
        scopeType: 'REGION' as const,
        scopeRegion: region,
        roleId,
      })),
    };
  }

  if (scopeType === 'market') {
    const marketMappings: LocationMappingPayload[] = [];
    const seen = new Set<string>();

    // Build a market→region lookup from the pre-computed tree first (fast path),
    // then fall back to scanning raw rows for resilience.
    const treeMarketToRegion = new Map<string, string>();
    locationTree?.forEach((regionNode) => {
      regionNode.markets.forEach((marketNode) => {
        treeMarketToRegion.set(marketNode.market.trim().toLowerCase(), regionNode.region);
      });
    });

    for (const market of normalizedSelections) {
      const marketKey = market.trim().toLowerCase();

      // 1. Try tree lookup (exact)
      let resolvedRegions: string[] = [];
      const treeRegion = treeMarketToRegion.get(marketKey);
      if (treeRegion) {
        resolvedRegions = [treeRegion];
      } else {
        // 2. Fall back to raw row scan (handles renamed / partial matches)
        const matchingRows = locationRows.filter((location: any) =>
          isSameText(String(location?.market ?? location?.marketName ?? ''), market),
        );
        resolvedRegions = Array.from(
          new Set(
            matchingRows
              .map((row: any) => String(row?.region ?? row?.regionName ?? '').trim())
              .filter(Boolean),
          ),
        );
      }

      if (!resolvedRegions.length) {
        // 3. Last resort: send market without region — backend can infer it
        console.warn(`[UserDetailPage] Could not resolve region for market "${market}", sending without scopeRegion`);
        const key = `unknown::${marketKey}`;
        if (!seen.has(key)) {
          seen.add(key);
          marketMappings.push({ scopeType: 'MARKET' as const, scopeMarket: market, roleId });
        }
        continue;
      }

      resolvedRegions.forEach((region) => {
        const key = `${region.toLowerCase()}::${marketKey}`;
        if (seen.has(key)) return;
        seen.add(key);
        marketMappings.push({
          scopeType: 'MARKET' as const,
          scopeRegion: region,
          scopeMarket: market,
          roleId,
        });
      });
    }

    return { mappings: marketMappings };
  }

  const siteMappings: LocationMappingPayload[] = [];
  const seenLocationIds = new Set<number>();

  for (const site of normalizedSelections) {
    const matchingRow = locationRows.find((location: any) => {
      const locationName = String(location?.name ?? location?.locName ?? location?.displayText ?? '').trim();
      return locationName && isSameText(locationName, site);
    });

    const locationIdValue = matchingRow?.locationId ?? matchingRow?.id;
    const numericLocationId = Number(locationIdValue);
    if (!Number.isFinite(numericLocationId)) {
      return { mappings: [], error: `Unable to resolve locationId for selected site "${site}".` };
    }

    if (seenLocationIds.has(numericLocationId)) continue;
    seenLocationIds.add(numericLocationId);

    siteMappings.push({
      scopeType: 'SITE' as const,
      locationId: numericLocationId,
      roleId,
    });
  }

  return { mappings: siteMappings };
};

const UserDetailPage: React.FC<UserDetailPageProps> = ({ onBack, onSave, onRemoveAccess, locationOptions = [], readOnly = false }) => {
  const dispatch = useDispatch<AppDispatch>();
  const isDarkTheme = useTheme().resolvedTheme === 'dark';
  const user = useSelector(selectSelectedUser);
  const hasUnsavedChanges = useSelector(selectHasUnsavedChanges);
  const updatedBy = useSelector((state: RootState) => (
    state.appData.userInfo?.email ??
    state.auth.user?.email ??
    state.auth.user?.username ??
    ''
  ));
  // Global /roles fetch (AppWrapper) — used to resolve privileged RoleIDs by name
  // instead of hardcoding them, since RoleIDs are environment-specific auto-increment values.
  const userManagementRoles = useSelector((state: RootState) => state.appData.userManagementRoles);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [roleOptionsData, setRoleOptionsData] = useState<DepartmentRoleOption[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  // Roles for the user's original department — needed to resolve originalAccessLevel's
  // scope when the department is changed mid-edit (roleOptionsData then reflects the new dept).
  const [originalDeptRoles, setOriginalDeptRoles] = useState<DepartmentRoleOption[]>([]);
  const [adminLocationRows, setAdminLocationRows] = useState<Array<any>>([]);
  const [accessibleModules, setAccessibleModules] = useState<Array<{ accessId: number; roleId: number; moduleId: number; moduleName: string; hasAccess: boolean }>>([]);
  const [accessibleLoading, setAccessibleLoading] = useState(false);
  // Snapshot of access level and locations when the detail page opened
  // Used to always display the "Current Access" baseline even while editing
  const [originalAccessLevel] = useState(() => user?.accessLevel ?? '');
  const [originalLocation] = useState<string[]>(() => user?.location ?? []);
  const [originalActiveStatus] = useState(() => user?.activeStatus ?? 'Inactive');
  const [originalOrganization] = useState(() => user?.organization ?? '');
  const [originalDepartment] = useState(() => user?.department ?? '');
  const [originalSustainTeamMember] = useState<boolean | null>(() => user?.sustainTeamMember ?? null);
  const [originalSustainTeamRole] = useState<'Member' | 'Lead' | null>(() => user?.sustainTeamRole ?? null);
  const [originalFundamentals] = useState(() => user?.fundamentals ?? null);
  const [originalLighthouse] = useState(() => user?.lighthouse ?? null);
  const [originalAdvFundamentals] = useState(() => user?.advFundamentals ?? null);
  const [originalNewStatus] = useState(() => user?.newStatus ?? false);
  const [originalAccessType] = useState(() => user?.accessType ?? deriveAccessType(user?.accessLevel));
  // Access Type Tier: mirrors UserAddModal.tsx. Derived once at mount (same convention as the
  // `original*` baselines above) from the user's current accessType; Field/3PL both mean
  // "Normal User" under the hood, Support means accessType is one of the full-access roles.

  // Cache for Normal User data when switching to Super User/Developer
  // Allows restoring values if user switches back without saving
  const [normalUserCache, setNormalUserCache] = useState<{
    organization?: string;
    department?: string;
    accessLevel?: string;
    location?: string[];
  } | null>(null);

  const [accessTypeTier, setAccessTypeTier] = useState<typeof ACCESS_TYPE_TIERS[number]>(() => {
    const currentAccessType = user?.accessType ?? deriveAccessType(user?.accessLevel);
    return currentAccessType === 'Normal User' ? 'Field' : 'Support';
  });
  const { supportTierRoles, supportTierRolesLoading } = useSupportTierRoles('user-management');
  // Any Support-tier role is full-access — don't hardcode specific role names.
  const showFullAccess = accessTypeTier === 'Support';

  // Helper function to derive access type from role
  function deriveAccessType(role?: string): string {
    return isSupportRoleName(role) ? (role as string) : 'Normal User';
  }

  // Fetch accessible modules whenever the selected role changes
  useEffect(() => {
    if (!user?.accessLevel) {
      setAccessibleModules([]);
      return;
    }
    // Support-tier roles have no department, so roleOptionsData is always empty —
    // resolve their roleId from supportTierRoles instead, same fix as the Access Request form.
    const matched =
      roleOptionsData.find((r) => normalizeRoleName(r.roleName) === normalizeRoleName(user.accessLevel)) ??
      supportTierRoles.find((r) => normalizeRoleName(r.roleName) === normalizeRoleName(user.accessLevel));
    if (matched == null || !Number.isFinite(matched.roleId)) {
      setAccessibleModules([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setAccessibleLoading(true);
      try {
        const rows = await getAccessibleModules(matched.roleId);
        if (!cancelled) setAccessibleModules(Array.isArray(rows) ? (rows as any[]) : []);
      } catch {
        if (!cancelled) setAccessibleModules([]);
      } finally {
        if (!cancelled) setAccessibleLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [user?.accessLevel, roleOptionsData, supportTierRoles]);

  const visibleAccessibleModules = useMemo(() => {
    const seen = new Set<string>();
    return accessibleModules.filter((mod) => {
      if (!mod.hasAccess) return false;
      const key = `${mod.moduleId}:${mod.moduleName.trim().toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [accessibleModules]);

  const locationTree = useMemo<LocationRegion[]>(() => {
    if (!adminLocationRows.length) return [];

    const regionMap = new Map<string, Map<string, Set<string>>>();
    adminLocationRows.forEach((loc: any) => {
      const region = String(loc.region ?? 'Unknown').trim() || 'Unknown';
      const market = String(loc.market ?? 'Unknown').trim() || 'Unknown';
      const site = String(loc.name ?? loc.locName ?? '').trim();
      if (!site || site === 'ALL') return;

      if (!regionMap.has(region)) regionMap.set(region, new Map());
      const marketMap = regionMap.get(region)!;
      if (!marketMap.has(market)) marketMap.set(market, new Set());
      marketMap.get(market)!.add(site);
    });

    return Array.from(regionMap.entries()).map(([region, marketMap]) => ({
      region,
      siteCount: Array.from(marketMap.values()).reduce((sum, sites) => sum + sites.size, 0),
      markets: Array.from(marketMap.entries()).map(([market, sites]) => ({
        market,
        siteCount: sites.size,
        sites: Array.from(sites).map((name) => ({ name })),
      })),
    }));
  }, [adminLocationRows]);

  const allSiteSelections = useMemo(
    () => Array.from(new Set(locationTree.flatMap((r) => r.markets.flatMap((m) => m.sites.map((s) => s.name))))),
    [locationTree],
  );

  // ── Access scope helpers ───────────────────────────────────────────────────
  const scopeLookupRoles = useMemo(
    () => [...roleOptionsData, ...originalDeptRoles],
    [roleOptionsData, originalDeptRoles],
  );
  const originalScopeInfo = getRoleScopeConfig(originalAccessLevel, scopeLookupRoles);
  const isOriginalFullAccess = originalScopeInfo?.type === 'full';
  const newScopeInfo = user ? getRoleScopeConfig(user.accessLevel, scopeLookupRoles) : null;
  const isNewFullAccess = newScopeInfo?.type === 'full';
  const pickerType = useMemo<LocationPickerType>(() => {
    if (!newScopeInfo || newScopeInfo.type === 'full') return 'site';
    return newScopeInfo.type;
  }, [newScopeInfo]);
  const roleDropdownOptions = useMemo(() => {
    const apiRoles = roleOptionsData.map((item) => item.roleName);
    if (user?.accessLevel && !apiRoles.includes(user.accessLevel)) {
      return [user.accessLevel, ...apiRoles];
    }
    return apiRoles;
  }, [roleOptionsData, user?.accessLevel]);

  const departmentOptions = useMemo(() => {
    if (!user?.organization) return [];
    return DEPARTMENT_OPTIONS[user.organization] || [];
  }, [user?.organization]);

  const organizationOptions = useMemo(() => Object.keys(DEPARTMENT_OPTIONS), []);

  // Ensure accessType is always in sync with the current role
  useEffect(() => {
    if (user && !user.accessType) {
      const derivedAccessType = deriveAccessType(user.accessLevel);
      dispatch(updateSelectedUser({ accessType: derivedAccessType }));
    }
  }, [user?.accessLevel, user?.accessType, dispatch]);

  useEffect(() => {
    if (!user?.department) {
      setRoleOptionsData([]);
      setRolesLoading(false);
      return;
    }

    let cancelled = false;
    const loadRoles = async () => {
      setRolesLoading(true);
      try {
        const api = createApiClient();
        const response = await api.get('/admin/users/roles', {
          params: { department: user.department },
          _skipLocationContext: true,
        } as any);

        const raw = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
          ? response.data.data
          : [];

        const normalized: DepartmentRoleOption[] = raw
          .map((item: any) => ({
            roleId: Number(item?.roleId),
            roleName: String(item?.roleName ?? '').trim(),
            scopeType: String(item?.scopeType ?? '').trim(),
          }))
          .filter((item: DepartmentRoleOption) => Number.isFinite(item.roleId) && item.roleName.length > 0);

        if (!cancelled) {
          setRoleOptionsData(normalized);
        }
      } catch {
        if (!cancelled) {
          setRoleOptionsData([]);
        }
      } finally {
        if (!cancelled) {
          setRolesLoading(false);
        }
      }
    };

    loadRoles();
    return () => {
      cancelled = true;
    };
  }, [user?.department]);

  // Fetch roles for the original department once, so the "Current Access" scope can
  // still be resolved correctly even if the department is changed during editing.
  useEffect(() => {
    if (!originalDepartment) {
      setOriginalDeptRoles([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const api = createApiClient();
        const response = await api.get('/admin/users/roles', {
          params: { department: originalDepartment },
          _skipLocationContext: true,
        } as any);
        const raw = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        const normalized: DepartmentRoleOption[] = raw
          .map((item: any) => ({
            roleId: Number(item?.roleId),
            roleName: String(item?.roleName ?? '').trim(),
            scopeType: String(item?.scopeType ?? '').trim(),
          }))
          .filter((item: DepartmentRoleOption) => Number.isFinite(item.roleId) && item.roleName.length > 0);
        if (!cancelled) setOriginalDeptRoles(normalized);
      } catch {
        if (!cancelled) setOriginalDeptRoles([]);
      }
    })();
    return () => { cancelled = true; };
  }, [originalDepartment]);

  useEffect(() => {
    // Check if user has Super User or Developer access type (not just role)
    const isPrivilegedAccessType = isSupportRoleName(user?.accessType);
    const hasNoOrganization = !user?.organization;

    // If no organization and not a privileged access type, don't fetch locations
    if (hasNoOrganization && !isPrivilegedAccessType) {
      setAdminLocationRows([]);
      return;
    }

    let cancelled = false;
    const loadLocations = async () => {
      try {
        const api = createApiClient();
        
        // For Super User/Developer with no organization, fetch all locations without organizationType filter
        // For Normal User, fetch locations filtered by organization
        const params: any = { page: 0, pageSize: 2000 };
        if (user?.organization) {
          params.organizationType = user.organization;
        }
        
        const response = await api.get('/admin/locations', {
          params,
          _skipLocationContext: true,
        } as any);

        if (!cancelled) {
          setAdminLocationRows(extractLocationRows(response));
        }
      } catch {
        if (!cancelled) {
          setAdminLocationRows([]);
        }
      }
    };

    loadLocations();
    return () => {
      cancelled = true;
    };
  }, [user?.organization, user?.accessType]);

  const handleAccessLevelChange = (newRole: string) => {
    const newAccessType = deriveAccessType(newRole);
    dispatch(updateSelectedUser({ accessLevel: newRole, accessType: newAccessType, location: [] }));
  };

  const handleAccessTypeChange = (newAccessType: string) => {
    const accessType = newAccessType;
    
    // Follow the same flow as Add User Page
    if (isSupportRoleName(accessType)) {
      // Cache current Normal User data before switching
      if (user?.accessType === 'Normal User') {
        setNormalUserCache({
          organization: user.organization,
          department: user.department,
          accessLevel: user.accessLevel,
          location: user.location,
        });
      }
      
      // For privileged access: set role to match access type, clear org/dept/locations
      // Locations will be auto-populated with all sites (handled in the save logic)
      dispatch(updateSelectedUser({ 
        accessType, 
        accessLevel: accessType,
        organization: '',
        department: '',
        location: [] 
      }));
      // Clear accessible modules and role options when switching to privileged access
      setAccessibleModules([]);
      setRoleOptionsData([]);
    } else {
      // Switching to Normal User
      const wasPrivileged = isSupportRoleName(user?.accessLevel);
      
      if (wasPrivileged && normalUserCache) {
        // Restore cached Normal User data if available
        dispatch(updateSelectedUser({ 
          accessType: 'Normal User',
          accessLevel: normalUserCache.accessLevel || '',
          organization: normalUserCache.organization || '',
          department: normalUserCache.department || '',
          location: normalUserCache.location || [] 
        }));
      } else {
        // No cache available, reset to empty state
        dispatch(updateSelectedUser({ 
          accessType: 'Normal User',
          accessLevel: '',
          organization: '',
          department: '',
          location: [] 
        }));
      }
      
      setAccessibleModules([]);
      setRoleOptionsData([]);
    }
  };

  const handleOrganizationChange = (value: string) => {
    dispatch(updateSelectedUser({ organization: value, department: '', accessLevel: '', location: [] }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.organization;
      delete newErrors.department;
      delete newErrors.accessLevel;
      delete newErrors.location;
      return newErrors;
    });
    setAccessibleModules([]);
    setRoleOptionsData([]);
  };

  const handleDepartmentChange = (value: string) => {
    dispatch(updateSelectedUser({ department: value, accessLevel: '', location: [] }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.department;
      delete newErrors.accessLevel;
      delete newErrors.location;
      return newErrors;
    });
    setAccessibleModules([]);
    setRoleOptionsData([]);
  };

  const handleRemoveAccessConfirm = async () => {
    if (!user || !onRemoveAccess) return;
    setIsRemoving(true);
    const success = await onRemoveAccess(user);
    setIsRemoving(false);
    if (success) setShowRemoveModal(false);
  };

  const handleFieldChange = (field: keyof User, value: any) => {
    dispatch(updateSelectedUser({ [field]: value }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleDateChange = (field: 'fundamentals' | 'lighthouse' | 'advFundamentals', date: Date | null) => {
    // Convert Date to string format (YYYY-MM-DD) in local timezone
    const dateString = date ? formatDateToString(date) : null;
    
    // Validate future date
    if (dateString && isFutureDate(dateString)) {
      setErrors(prev => ({
        ...prev,
        [field]: 'Training date cannot be in the future',
      }));
      return;
    }
    
    handleFieldChange(field, dateString);
  };

  const handleClearDate = (field: 'fundamentals' | 'lighthouse' | 'advFundamentals') => {
    handleFieldChange(field, null);
  };

  const handleSave = async () => {
    if (!user) return;
    
    // Validation
    const newErrors: Record<string, string> = {};
    
    // For Normal User access type, validate organization, department, role and locations
    const isPrivilegedAccess = isSupportRoleName(user.accessType);

    if (showFullAccess && !isPrivilegedAccess) {
      newErrors.accessLevel = 'Please select a role for Support access.';
    }
    
    if (!isPrivilegedAccess) {
      // Normal User validations
      if (!user.organization || user.organization.trim() === '') {
        newErrors.organization = 'Please select an organization.';
      }
      if (!user.department || user.department.trim() === '') {
        newErrors.department = 'Please select a department.';
      }
      if (!user.accessLevel || user.accessLevel.trim() === '') {
        newErrors.accessLevel = 'Please select a role for Normal User access.';
      }
      
      const effectiveLocations = newScopeInfo?.type === 'full' ? allSiteSelections : user.location;
      // For non-full-access roles, validate that locations are selected
      if (newScopeInfo && newScopeInfo.type !== 'full' && effectiveLocations.length === 0) {
        newErrors.location = 'Select at least one location for the chosen role scope.';
      }
      // For full-access roles under Normal User (like COE), we don't require allSiteSelections to be loaded
      // The backend handles full access via locationMappings with just roleId (similar to Super User/Developer)
    } else {
      // For Super User/Developer, ensure locations are loaded
      if (allSiteSelections.length === 0) {
        newErrors.location = 'Location master data is still loading. Please wait a moment and try again.';
      }
    }
    
    // Check training dates
    if (user.fundamentals && isFutureDate(user.fundamentals)) {
      newErrors.fundamentals = 'Cannot be in the future';
    }
    if (user.lighthouse && isFutureDate(user.lighthouse)) {
      newErrors.lighthouse = 'Cannot be in the future';
    }
    if (user.advFundamentals && isFutureDate(user.advFundamentals)) {
      newErrors.advFundamentals = 'Cannot be in the future';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Call handler from parent (uses hook)
    setIsSaving(true);
    const payload: UserUpdatePayload = {};

    // For privileged access, always use all sites
    const effectiveLocations = isPrivilegedAccess ? allSiteSelections : (newScopeInfo?.type === 'full' ? allSiteSelections : user.location);

    const originalLocationsSnapshot = originalLocation
      .slice()
      .sort((left, right) => left.localeCompare(right));
    const currentLocationsSnapshot = effectiveLocations
      .slice()
      .sort((left, right) => left.localeCompare(right));

    const accessLevelChanged = originalAccessLevel !== user.accessLevel;
    const accessTypeChanged = originalAccessType !== (user.accessType || deriveAccessType(user.accessLevel));
    // Locations are fixed/computed (all sites) for full-access roles and are not
    // user-editable in that case (selectionLocked), so they should never count as
    // "changed" on their own — only an explicit role change should trigger the
    // location-mapping resolution path for full-access users.
    const isFullAccessScope = newScopeInfo?.type === 'full';
    const locationsChanged =
      originalActiveStatus !== user.activeStatus ||
      (!isFullAccessScope &&
        (originalLocationsSnapshot.length !== currentLocationsSnapshot.length ||
          originalLocationsSnapshot.some((location, index) => location !== currentLocationsSnapshot[index])));

    // Only resolve role ID + build location mappings when access actually changed.
    // Non-access edits (sustain team, training dates, newStatus, active status)
    // must not be blocked by role-option data that loads asynchronously.
    if (accessLevelChanged || accessTypeChanged || locationsChanged) {
      let matchedRoleId: number | null = null;

      // For Support-tier roles, resolve the role ID by name (environment-specific), falling back to hardcoded IDs
      if (isPrivilegedAccess && user.accessType) {
        const matchedPrivilegedRole = userManagementRoles.find(
          (role) => normalizeRoleName(role.roleName) === normalizeRoleName(user.accessType as string),
        );
        matchedRoleId = matchedPrivilegedRole?.roleId ?? PRIVILEGED_ROLE_IDS[user.accessType] ?? null;
        if (!matchedRoleId) {
          setErrors((prev) => ({
            ...prev,
            accessLevel: `Unable to resolve role ID for ${user.accessType}.`,
          }));
          setIsSaving(false);
          return;
        }
      } else {
        // For Normal User, look up role in roleOptionsData
        const matchedRole = roleOptionsData.find(
          (role) => normalizeRoleName(role.roleName) === normalizeRoleName(user.accessLevel),
        );

        if (!matchedRole || !Number.isFinite(matchedRole.roleId)) {
          setErrors((prev) => ({
            ...prev,
            accessLevel: 'Unable to resolve role ID for the selected role.',
          }));
          setIsSaving(false);
          return;
        }
        matchedRoleId = matchedRole.roleId;
      }

      // For privileged access (Super User/Developer), use simple full-access mapping
      // API only needs: role, activeStatus, locationMappings
      if (isPrivilegedAccess) {
        payload.role = user.accessLevel;
        payload.activeStatus = user.activeStatus === 'Active';
        payload.locationMappings = [{ roleId: matchedRoleId }];
        // Do NOT send: accessType, organization, department, locations for privileged access
      } else {
        // Normal User - resolve scope and build location mappings
        if (!newScopeInfo) {
          setErrors((prev) => ({
            ...prev,
            accessLevel: 'Unable to resolve scope for the selected role.',
          }));
          setIsSaving(false);
          return;
        }

        // For full-access roles (like COE), send simple mapping like Super User/Developer
        if (newScopeInfo.type === 'full') {
          payload.role = user.accessLevel;
          payload.accessType = user.accessType || deriveAccessType(user.accessLevel);
          payload.organization = user.organization;
          payload.department = user.department;
          payload.activeStatus = user.activeStatus === 'Active';
          payload.locationMappings = [{ roleId: matchedRoleId }];
          // Do NOT send locations array for full-access roles
        } else {
          // For scoped roles (region/market/site), build proper location mappings
          const { mappings, error: mappingError } = buildLocationMappings(
            newScopeInfo.type,
            effectiveLocations,
            matchedRoleId,
            adminLocationRows,
            locationTree,
          );

          if (mappingError) {
            setErrors((prev) => ({
              ...prev,
              location: mappingError,
            }));
            setIsSaving(false);
            return;
          }

          payload.role = user.accessLevel;
          payload.accessType = user.accessType || deriveAccessType(user.accessLevel);
          payload.organization = user.organization;
          payload.department = user.department;
          payload.activeStatus = user.activeStatus === 'Active';
          payload.locationMappings = mappings;
          payload.locations = effectiveLocations.map((location) => ({
            location,
            activeStatus: user.activeStatus === 'Active',
          }));
        }
      }
    } else {
      // Access unchanged — check if it's privileged or normal
      const currentIsPrivileged = isSupportRoleName(user.accessType);
      
      if (currentIsPrivileged) {
        // For privileged access, only send activeStatus (role unchanged)
        payload.activeStatus = user.activeStatus === 'Active';
        // Do NOT send: accessType, organization, department, locations
      } else {
        // For Normal User, send all fields
        payload.activeStatus = user.activeStatus === 'Active';
        payload.accessType = user.accessType || deriveAccessType(user.accessLevel);
        payload.organization = user.organization || '';
        payload.department = user.department || '';
      }
    }

    // Always send organization and department so changes made via the dropdowns
    // are persisted even when the role itself didn't change.
    payload.organization = user.organization;
    payload.department = user.department;

    payload.sustainTeamMember = user.sustainTeamMember;
    payload.sustainTeamRole = user.sustainTeamMember === true
      ? (user.sustainTeamRole ?? 'Member')
      : null;
    payload.fundamentals = user.fundamentals;
    payload.lighthouse = user.lighthouse;
    payload.advFundamentals = user.advFundamentals;
    payload.newStatus = user.newStatus;

    const request: UserUpdateRequest = {
      userEmail: user.userEmail,
      user,
      payload: {
        ...payload,
        updatedBy,
      },
    };

    console.log('[UserDetailPage] Save request payload:', JSON.stringify(request, null, 2));

    const success = await onSave(request);
    setIsSaving(false);
    
    if (success) {
      // Clear cache on successful save
      setNormalUserCache(null);
      // Navigate back to User Management list
      onBack();
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowDiscardModal(true);
    } else {
      onBack();
    }
  };

  const handleDiscardConfirm = () => {
    setShowDiscardModal(false);
    dispatch(discardChanges());
    onBack();
  };

  const handleDiscardCancel = () => {
    setShowDiscardModal(false);
  };

  if (!user) return null;

  return (
    <>
      {/* Remove Access Confirmation Modal */}
      <Modal
        visible={showRemoveModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowRemoveModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalBox}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Remove Access</Text>
              <TouchableOpacity onPress={() => setShowRemoveModal(false)} style={styles.modalCloseBtn}>
                <Text style={styles.modalCloseText}>✕</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.modalDivider} />
            <Text style={styles.modalMessage}>
              Remove all Navigator access for {user.userEmail}?{`\n\n`}This will revoke their role and location assignments. They will need to submit a new access request through the Ticketing Portal to regain access.
            </Text>
            <View style={styles.modalDivider} />
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.modalBtnSecondary} onPress={() => setShowRemoveModal(false)} disabled={isRemoving}>
                <Text style={styles.modalBtnSecondaryText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalBtnRemove} onPress={handleRemoveAccessConfirm} disabled={isRemoving}>
                <Text style={styles.modalBtnRemoveText}>{isRemoving ? 'Removing…' : 'Remove Access'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={showDiscardModal}
        transparent
        animationType="fade"
        onRequestClose={handleDiscardCancel}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalBox}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Discard Changes?</Text>
              <TouchableOpacity onPress={handleDiscardCancel} style={styles.modalCloseBtn}>
                <Text style={styles.modalCloseText}>✕</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.modalDivider} />
            <Text style={styles.modalMessage}>Any unsaved modifications will be lost.</Text>
            <View style={styles.modalDivider} />
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.modalBtnSecondary} onPress={handleDiscardCancel}>
                <Text style={styles.modalBtnSecondaryText}>Stay on Page</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalBtnPrimary} onPress={handleDiscardConfirm}>
                <Text style={styles.modalBtnPrimaryText}>Discard Changes</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

    <ScrollView 
      style={styles.container} 
      showsVerticalScrollIndicator={true}
    >
      {/* Page Header */}
      <View style={styles.headerSection}>
        <View style={styles.pageHeader}>
          <AdminButton label="← Back" variant="secondary" onPress={handleCancel} />
          <Text style={styles.pageTitle}>User Details</Text>
        </View>
      </View>

      {/* Read-Only Section */}
      <View style={styles.section}>
       
        <View style={styles.readOnlyGrid}>
          <View style={styles.readOnlyField}>
            <Text style={styles.readOnlyLabel}>User Email</Text>
            <Text style={styles.readOnlyValue}>{user.userEmail}</Text>
          </View>
          
          <View style={styles.readOnlyField}>
            <Text style={styles.readOnlyLabel}>GPID</Text>
            <Text style={styles.readOnlyValue}>{user.gpid}</Text>
          </View>

          <View style={styles.readOnlyField}>
            <Text style={styles.readOnlyLabel}>Last Update Time</Text>
            <Text style={styles.readOnlyValue}>{formatDate(user.lastUpdateTime)}</Text>
          </View>
          
          <View style={styles.readOnlyField}>
            <Text style={styles.readOnlyLabel}>Navi Join Date</Text>
            <Text style={styles.readOnlyValue}>{formatDate(user.naviJoinDate)}</Text>
          </View>
        </View>
      </View>

      {/* Editable Fields Section */}
      <View style={styles.section} pointerEvents={readOnly ? 'none' : 'auto'}>
        <Text style={styles.sectionTitle}>Access & Status</Text>
        
        {/* Current Access Baseline – read-only snapshot */}
        <View style={styles.baselineBox}>
          <Text style={styles.baselineLabel}>Current Access</Text>
          <View style={styles.baselineRow}>
            <View style={styles.baselineField}>
              <Text style={styles.readOnlyLabel}>Current Role</Text>
              <Text style={styles.readOnlyValue}>{originalAccessLevel || user.accessLevel}</Text>
            </View>
            <View style={styles.baselineField}>
              <Text style={styles.readOnlyLabel}>Access Type</Text>
              {originalScopeInfo ? (
                <View style={[styles.accessTypeBadge, isOriginalFullAccess ? styles.accessTypeBadgeFull : styles.accessTypeBadgeLimited]}>
                  <Text style={[styles.accessTypeBadgeText, isOriginalFullAccess ? styles.accessTypeBadgeTextFull : styles.accessTypeBadgeTextLimited]}>
                    {originalScopeInfo.accessLabel}
                  </Text>
                </View>
              ) : (
                <Text style={styles.readOnlyValue}>—</Text>
              )}
            </View>
            <View style={styles.baselineField}>
              <Text style={styles.readOnlyLabel}>
                {originalScopeInfo?.type === 'region'
                  ? 'Current Region(s)'
                  : originalScopeInfo?.type === 'market'
                  ? 'Current Market(s)'
                  : 'Current Location(s)'}
              </Text>
              <View style={styles.baselineChips}>
                {isOriginalFullAccess ? (
                  <View style={styles.baselineChip}><Text style={styles.baselineChipText}>All Locations</Text></View>
                ) : originalLocation.length > 0 ? (
                  originalLocation.map((loc: string, i: number) => (
                    <View key={i} style={styles.baselineChip}><Text style={styles.baselineChipText}>{loc}</Text></View>
                  ))
                ) : (
                  <Text style={styles.readOnlyValue}>—</Text>
                )}
              </View>
            </View>
          </View>
        </View>

        {/* Modify Access header + Remove Access button */}
        <View style={styles.modifyAccessRow}>
          <Text style={styles.modifyAccessTitle}>Modify Access</Text>
          {onRemoveAccess && !readOnly && (
            <TouchableOpacity style={styles.removeBtn} onPress={() => setShowRemoveModal(true)}>
              <Text style={styles.removeBtnText}>Remove Access</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Access Type Tier selection first (like Add User page) */}
        <View style={styles.modifyAccessField}>
          <AdminFormField label="Access Type Tier">
            <UserFormSelect
              value={accessTypeTier}
              options={[...ACCESS_TYPE_TIERS]}
              placeholder="Select access type tier..."
              onChange={(value) => {
                const tier = value as typeof ACCESS_TYPE_TIERS[number];
                setAccessTypeTier(tier);
                // Only reset when leaving a Support role; Field <-> 3PL keeps the Normal User selections.
                if (tier !== 'Support' && user.accessType !== 'Normal User') {
                  handleAccessTypeChange('Normal User');
                }
              }}
            />
          </AdminFormField>
          <Text style={styles.fieldHint}>
            Field and 3PL/Outside Labor use Organization, Department, and Role below. Support roles get access to everything.
          </Text>
        </View>

        {accessTypeTier === 'Support' ? (
          <View style={styles.modifyAccessField}>
            <AdminFormField label="Role" error={errors.accessLevel}>
              <UserFormSelect
                value={isSupportRoleName(user.accessType) ? (user.accessType as string) : (isSupportRoleName(user.accessLevel) ? user.accessLevel : '')}
                options={supportTierRoles.map((r) => r.roleName)}
                placeholder={supportTierRolesLoading ? 'Loading roles...' : 'Select role...'}
                onChange={handleAccessTypeChange}
              />
            </AdminFormField>
          </View>
        ) : null}

        {/* Full-access banner for Support-tier roles */}
        {showFullAccess && isSupportRoleName(user.accessType) ? (
          <View style={[styles.fullAccessBanner, { marginBottom: 12 }]}>
            <MaterialIcons name="shield" size={16} color={AdminColors.green600} />
            <Text style={styles.fullAccessText}>
              <Text style={{ fontWeight: '700' }}>{user.accessType}</Text> access grants access to all locations globally.
            </Text>
          </View>
        ) : null}

        {/* Normal User: Organization, Department, Role selection */}
        {!showFullAccess ? (
          <>
            <View style={styles.modifyAccessField}>
              <AdminFormField label="Organization" required error={errors.organization}>
                <UserFormSelect
                  value={user.organization || ''}
                  options={organizationOptions}
                  placeholder="Select organization..."
                  onChange={handleOrganizationChange}
                />
              </AdminFormField>
              <Text style={styles.fieldHint}>Scopes which locations are available downstream.</Text>
            </View>

            <View style={styles.modifyAccessField}>
              <AdminFormField label="Department" required error={errors.department}>
                <UserFormSelect
                  value={user.department || ''}
                  options={departmentOptions}
                  placeholder={user.organization ? 'Select a department...' : 'Select an organization first'}
                  onChange={handleDepartmentChange}
                  disabled={!user.organization}
                />
              </AdminFormField>
              <Text style={styles.fieldHint}>Determines the available roles.</Text>
            </View>

            <View style={styles.modifyAccessField}>
              <AdminFormField label="Role" required error={errors.accessLevel}>
                <UserFormSelect
                  value={user.accessLevel}
                  options={roleDropdownOptions}
                  placeholder={
                    !user.department
                      ? 'Select a department first'
                      : rolesLoading
                      ? 'Loading roles...'
                      : roleDropdownOptions.length
                      ? 'Select a role...'
                      : 'No roles available for this department'
                  }
                  onChange={handleAccessLevelChange}
                  disabled={!user.department || rolesLoading || roleDropdownOptions.length === 0}
                />
              </AdminFormField>
              {newScopeInfo ? (
                <Text style={styles.scopeLine}>
                  Role scope: <Text style={styles.scopeAccent}>{newScopeInfo.accessLabel}</Text> - {newScopeInfo.scopeLabel}
                </Text>
              ) : null}
            </View>
          </>
        ) : null}

        {/* Location section - only shown for Normal User */}
        {!showFullAccess && newScopeInfo ? (
          <View style={styles.locationSection}>
            <AdminFormField label="Location(s)" error={errors.location}>
              <AccessRequestLocationPicker
                pickerType={newScopeInfo.type === 'full' ? 'site' : pickerType}
                organization={user.organization || ''}
                selectedItems={newScopeInfo.type === 'full' ? allSiteSelections : user.location}
                onSelectionChange={(locations) => handleFieldChange('location', locations)}
                locationTree={locationTree}
                selectionLocked={newScopeInfo.type === 'full'}
              />
            </AdminFormField>

            {newScopeInfo.type === 'full' ? (
              <View style={styles.fullAccessBanner}>
                <MaterialIcons name="verified-user" size={16} color={AdminColors.green600} />
                <Text style={styles.fullAccessText}>
                  All locations selected - {locationTree.length} regions - {locationTree.reduce((sum, region) => sum + region.markets.length, 0)} markets - {allSiteSelections.length} sites.
                </Text>
              </View>
            ) : null}
          </View>
        ) : null}

        {/* Accessible Modules Card */}
        <View style={styles.modulesCard}>
          <View style={styles.modulesCardHeader}>
            <MaterialIcons name="info-outline" size={16} color="#1d4ed8" />
            <View style={{ flex: 1, marginLeft: 8 }}>
              <Text style={styles.modulesCardTitle}>What does this access provide?</Text>
              <Text style={styles.modulesCardSubtitle}>Based on the selected role's privileges</Text>
            </View>
          </View>

          <View style={styles.modulesSameAccessBadge}>
            <Text style={styles.modulesSameAccessBadgeText}>SAME ACCESS AT ALL SELECTED SITES</Text>
          </View>

          <Text style={styles.modulesAccessLabel}>With this role, the user will have access to:</Text>

          <View style={styles.modulesPillsRow}>
            {accessibleLoading ? (
              <Text style={styles.modulesAccessLabel}>Loading modules...</Text>
            ) : visibleAccessibleModules.length > 0 ? (
              visibleAccessibleModules.map((mod) => (
                <View key={mod.accessId} style={styles.modulePill}>
                  <Text style={styles.modulePillText}>{mod.moduleName}</Text>
                </View>
              ))
            ) : (
              <Text style={styles.modulesAccessLabel}>Select a role to view accessible modules.</Text>
            )}
          </View>
        </View>

        {/* Row 2: Active Status, Sustain Team Member, and Is New Toggles */}
        <View style={styles.togglesGrid}>
          <View style={styles.toggleGridItem}>
            <AdminFormField label="Active Status" required error={errors.activeStatus}>
              <View style={styles.toggleRow}>
                <AdminToggle
                  value={user.activeStatus === 'Active'}
                  onChange={(value: boolean) => handleFieldChange('activeStatus', value ? 'Active' : 'Inactive')}
                />
                <Text style={styles.toggleStatusLabel}>{user.activeStatus}</Text>
              </View>
            </AdminFormField>
          </View>

          <View style={styles.toggleGridItem}>
            <AdminFormField label="Sustain Team Member" error={errors.sustainTeamMember}>
              <View style={styles.toggleRow}>
                <AdminToggle
                  value={user.sustainTeamMember === true}
                  onChange={(value: boolean) => {
                    // When enabling for first time, set role to 'Member' by default
                    // When re-enabling, previous role will be restored by backend
                    // When disabling, set to false (role data preserved in backend)
                    if (value) {
                      // Enabling: if no role exists, set default to 'Member'
                      handleFieldChange('sustainTeamMember', true);
                      if (!user.sustainTeamRole) {
                        handleFieldChange('sustainTeamRole', 'Member');
                      }
                    } else {
                      // Disabling: just toggle off (role preserved in backend)
                      handleFieldChange('sustainTeamMember', false);
                    }
                  }}
                />
                <Text style={styles.toggleStatusLabel}>
                  {user.sustainTeamMember === true ? 'Yes' : 'No'}
                </Text>
              </View>
            </AdminFormField>
          </View>

          <View style={styles.toggleGridItem}>
            <AdminFormField label="New Status" error={errors.newStatus}>
              <View style={styles.toggleRow}>
                <AdminToggle
                  value={user.newStatus}
                  onChange={(value: boolean) => handleFieldChange('newStatus', value)}
                />
                <Text style={styles.toggleStatusLabel}>{user.newStatus ? 'Yes' : 'No'}</Text>
              </View>
            </AdminFormField>
          </View>
        </View>
      </View>

      {/* Training Dates Section */}
      <View style={styles.section} pointerEvents={readOnly ? 'none' : 'auto'}>
        <Text style={styles.sectionTitle}>Training Completion Dates</Text>
        
        <View style={styles.trainingGrid}>
          {/* Fundamentals */}
          <View style={styles.gridItem}>
            <AdminFormField label="Fundamentals Completion Date" error={errors.fundamentals}>
              <LhDatePicker
                value={user.fundamentals ? new Date(user.fundamentals) : null}
                onChange={(date: Date | null) => handleDateChange('fundamentals', date)}
                placeholder="dd-mm-yyyy"
                colorScheme={isDarkTheme ? 'dark' : 'light'}
                iconColor={isDarkTheme ? '#FFFFFF' : AdminColors.blue700}
                disabledIconColor={AdminColors.gray200}
              />
              <Text style={styles.fieldHelper}>Leave blank if training has not been completed.</Text>
              <TouchableOpacity onPress={() => handleClearDate('fundamentals')}>
                <Text style={styles.clearLink}>Clear Date</Text>
              </TouchableOpacity>
            </AdminFormField>
          </View>

          {/* Lighthouse */}
          <View style={styles.gridItem}>
            <AdminFormField label="Lighthouse Completion Date" error={errors.lighthouse}>
              <LhDatePicker
                value={user.lighthouse ? new Date(user.lighthouse) : null}
                onChange={(date: Date | null) => handleDateChange('lighthouse', date)}
                placeholder="dd-mm-yyyy"
                colorScheme={isDarkTheme ? 'dark' : 'light'}
                iconColor={isDarkTheme ? '#FFFFFF' : AdminColors.blue700}
                disabledIconColor={AdminColors.gray200}
              />
              <Text style={styles.fieldHelper}>Leave blank if training has not been completed.</Text>
              <TouchableOpacity onPress={() => handleClearDate('lighthouse')}>
                <Text style={styles.clearLink}>Clear Date</Text>
              </TouchableOpacity>
            </AdminFormField>
          </View>

          {/* Advanced Fundamentals */}
          <View style={styles.gridItem}>
            <AdminFormField label="Adv Fundamentals Completion Date" error={errors.advFundamentals}>
              <LhDatePicker
                value={user.advFundamentals ? new Date(user.advFundamentals) : null}
                onChange={(date: Date | null) => handleDateChange('advFundamentals', date)}
                placeholder="dd-mm-yyyy"
                colorScheme={isDarkTheme ? 'dark' : 'light'}
                iconColor={isDarkTheme ? '#FFFFFF' : AdminColors.blue700}
                disabledIconColor={AdminColors.gray200}
              />
              <Text style={styles.fieldHelper}>Leave blank if training has not been completed.</Text>
              <TouchableOpacity onPress={() => handleClearDate('advFundamentals')}>
                <Text style={styles.clearLink}>Clear Date</Text>
              </TouchableOpacity>
            </AdminFormField>
          </View>
        </View>
      </View>

      {/* Save / Cancel Buttons */}
      <View style={styles.bottomActions}>
        <AdminButton 
          label="Cancel" 
          variant="secondary" 
          onPress={handleCancel}
          disabled={isSaving}
        />
        <AdminButton 
          label={isSaving ? "Saving..." : "Save Changes"}
          variant="primary" 
          onPress={handleSave}
          disabled={!hasUnsavedChanges || isSaving || readOnly}
        />
      </View>
    </ScrollView>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: AdminColors.bgPage,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalBox: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: 420,
    maxWidth: '90%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 16,
    elevation: 8,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 16,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
  },
  modalCloseBtn: {
    padding: 4,
  },
  modalCloseText: {
    fontSize: 16,
    color: AdminColors.gray450,
  },
  modalDivider: {
    height: 1,
    backgroundColor: AdminColors.borderTable,
  },
  modalMessage: {
    fontSize: 14,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    paddingHorizontal: 24,
    paddingVertical: 20,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 12,
    paddingHorizontal: 24,
    paddingVertical: 20,
  },
  modalBtnSecondary: {
    borderWidth: 2,
    borderColor: AdminColors.blue700,
    borderRadius: 24,
    paddingVertical: 10,
    paddingHorizontal: 24,
  },
  modalBtnSecondaryText: {
    fontSize: 14,
    fontWeight: '700',
    color: AdminColors.blue700,
    fontFamily: FontFamily.bold,
  },
  modalBtnPrimary: {
    backgroundColor: AdminColors.blue700,
    borderRadius: 24,
    paddingVertical: 10,
    paddingHorizontal: 24,
  },
  modalBtnPrimaryText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#fff',
    fontFamily: FontFamily.bold,
  },
  modalBtnRemove: {
    backgroundColor: AdminColors.textError,
    borderRadius: 24,
    paddingVertical: 10,
    paddingHorizontal: 24,
  },
  modalBtnRemoveText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#fff',
    fontFamily: FontFamily.bold,
  },
  // ─ Current Access Baseline ─────────────────────────────────────────────────
  baselineBox: {
    backgroundColor: '#FAFBFD',
    borderWidth: 1,
    borderColor: AdminColors.borderTable,
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  baselineLabel: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: AdminColors.gray450,
    marginBottom: 10,
    fontFamily: FontFamily.bold,
  },
  baselineRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 24,
    alignItems: 'flex-start',
  },
  baselineField: {
    flexDirection: 'column',
    gap: 4,
    minWidth: 160,
    flex: 1,
  },
  baselineChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 5,
    marginTop: 2,
  },
  baselineChip: {
    paddingHorizontal: 10,
    paddingVertical: 3,
    backgroundColor: AdminColors.gray25,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 100,
  },
  baselineChipText: {
    fontSize: 11.5,
    fontWeight: '600',
    color: AdminColors.gray500,
    fontFamily: FontFamily.bold,
  },
  // ─ Access type badge ────────────────────────────────────────────────────
  accessTypeBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 9,
    paddingVertical: 3,
    borderRadius: 12,
  },
  accessTypeBadgeFull: {
    backgroundColor: '#ede7f6',
  },
  accessTypeBadgeLimited: {
    backgroundColor: '#e0f2f1',
  },
  accessTypeBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
  },
  accessTypeBadgeTextFull: {
    color: '#5b3da8',
  },
  accessTypeBadgeTextLimited: {
    color: '#06756b',
  },
  // ─ Modify Access section ──────────────────────────────────────────────
  modifyAccessRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  modifyAccessTitle: {
    fontSize: 11,
    fontWeight: '700',
    color: AdminColors.gray450,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    fontFamily: FontFamily.bold,
  },
  removeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 100,
    borderWidth: 1.5,
    borderColor: AdminColors.textError,
    backgroundColor: '#fff',
  },
  removeBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: AdminColors.textError,
    fontFamily: FontFamily.bold,
  },
  modifyAccessField: {
    marginBottom: 8,
  },
  scopeLine: {
    fontSize: 12,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
    marginTop: 6,
  },
  scopeAccent: {
    fontSize: 12,
    color: AdminColors.blue700,
    fontFamily: FontFamily.bold,
  },
  locationSection: {
    marginBottom: 14,
  },
  fullAccessBanner: {
    marginTop: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: '#ecfdf3',
    borderWidth: 1,
    borderColor: '#b7ebcd',
  },
  fullAccessText: {
    flex: 1,
    fontSize: 12,
    color: '#1e7a46',
    fontFamily: FontFamily.regular,
  },
  locationChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 6,
  },
  locationChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 4,
    backgroundColor: AdminColors.blue25,
    borderWidth: 1,
    borderColor: AdminColors.blue50,
    borderRadius: 100,
  },
  locationChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: AdminColors.blue900,
    fontFamily: FontFamily.bold,
  },
  locationChipX: {
    fontSize: 14,
    color: AdminColors.gray350,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
  },
  locationCaption: {
    fontSize: 11,
    color: AdminColors.gray450,
    marginTop: 4,
    fontFamily: FontFamily.regular,
  },
  modulesCard: {
    borderWidth: 1,
    borderColor: '#bfd5f3',
    backgroundColor: '#f3f8ff',
    borderRadius: 10,
    padding: 14,
    marginBottom: 14,
  },
  modulesCardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  modulesCardTitle: {
    fontSize: 14,
    lineHeight: 18,
    color: '#173f91',
    fontFamily: FontFamily.bold,
  },
  modulesCardSubtitle: {
    fontSize: 12,
    lineHeight: 16,
    color: '#2563eb',
    fontFamily: FontFamily.regular,
    marginTop: 2,
  },
  modulesSameAccessBadge: {
    alignSelf: 'flex-start',
    marginTop: 10,
    borderRadius: 999,
    backgroundColor: '#d5f5dc',
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  modulesSameAccessBadgeText: {
    fontSize: 11,
    lineHeight: 14,
    color: '#0f7a36',
    fontFamily: FontFamily.bold,
  },
  modulesAccessLabel: {
    marginTop: 10,
    fontSize: 12,
    lineHeight: 16,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
  },
  modulesPillsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  modulePill: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#d8e4fb',
    backgroundColor: '#ffffff',
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  modulePillText: {
    fontSize: 11,
    lineHeight: 14,
    color: '#1f3f91',
    fontFamily: FontFamily.bold,
  },
  locationCaptionMax: {
    color: AdminColors.textError,
  },
  headerSection: {
    marginBottom: 20,
  },
  pageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  pageTitle: {
    fontSize: 28,
    lineHeight: 40,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
  },
  bottomActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    paddingBottom: 32,
  },
  section: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
    marginBottom: 14,
    paddingBottom: 6,
    borderBottomWidth: 2,
    borderBottomColor: AdminColors.borderTable,
  },
  readOnlyGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: AdminColors.nearWhite,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: AdminColors.gray25,
  },
  readOnlyField: {
    width: '48%',
    minWidth: 200,
    gap: 3,
    paddingVertical: 6,
  },
  readOnlyFieldInline: {
    gap: 5,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: AdminColors.nearWhite,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: AdminColors.gray25,
  },
  readOnlyLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: AdminColors.gray450,
    fontFamily: FontFamily.bold,
    textTransform: 'uppercase',
  },
  readOnlyValue: {
    fontSize: 13,
    lineHeight: 18,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
  },
  readOnlyValueBlue: {
    fontSize: 13,
    lineHeight: 18,
    color: AdminColors.blue700,
    fontFamily: FontFamily.regular,
    fontWeight: '500',
  },
  accessStatusGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
  },
  gridItem: {
    width: '48%', // Approximately 50% accounting for gap
    minWidth: 200,
  },
  togglesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
    marginTop: 14,
  },
  toggleGridItem: {
    width: '31%', // Three items per row (31% * 3 + gaps = ~100%)
    minWidth: 180,
  },
  trainingGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
  },
  formGrid: {
    gap: 14,
  },
  fieldHelper: {
    fontSize: 11,
    lineHeight: 14,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
    marginTop: 4,
  },
  fieldHint: {
    marginTop: -6,
    marginBottom: 8,
    fontSize: 11,
    lineHeight: 14,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    height: 36,
  },
  toggleStatusLabel: {
    fontSize: 13,
    lineHeight: 18,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
  },
  clearLink: {
    fontSize: 11,
    fontWeight: '600',
    color: AdminColors.textError,
    textDecorationLine: 'underline',
    fontFamily: FontFamily.bold,
    marginTop: 4,
  },
  helperText: {
    fontSize: 11,
    lineHeight: 14,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
    marginTop: 8,
  },
});

export default UserDetailPage;
